import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Copy, Trash2 } from "lucide-react";
import SpreadsheetItemInput from "../select/SpreadsheetItemInput";
import { useItemCache } from "../context/ItemCacheContext";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { usePhaseTypeOptions } from "../select/PhaseTypeSelect";
import { useSubprocesses } from "../select/SubprocessCells";
import { parseDecimalInput, formatQtyForDisplay, formatUnitPrice } from "../utils/decimalInput";
import useSpreadsheetKeyboard from "../hooks/useSpreadsheetKeyboard";
import { useTableViewSort } from "../hooks/useTableViewSort";
import { useTableSearchHighlight, SearchCell } from "../hooks/useTableSearchHighlight";
import TableSortTh from "./TableSortTh";
import TableViewToolbar from "./TableViewToolbar";
import ColumnManager, { ColumnDef } from "./ColumnManager";
import RNRazmerCellSaveMark from "./RNRazmerCellSaveMark";
import RNRazmerLotLines from "./RNRazmerLotLines";
import { RNRazmerLotMap } from "../hooks/useRNRazmerLots";
import { prepareRNRazmerLotConfirmation } from "../hooks/useRNRazmerLotConfirmation";
import {
  RazmerAutosaveField,
  RazmerCellSaveStatusMap,
  razmerAutosaveCellKey,
} from "../hooks/useRNRazmerAutosave";
import { useTranslation } from "react-i18next";
import OSCard, { OSCardNotice, OS_PLAIN_DISABLED_CLS, OS_PLAIN_DISABLED_SELECT_CLS } from "./OSCard";
import { buildProcessGroupOrder, processGroupIdForSubprocess, processGroupRank as getProcessGroupRank } from "../utils/processGroupOrder";

// Размерување table (mockup razmer stage), controlled by OS.tsx and persisted per
// ident. Uses the shared spreadsheet keyboard nav (arrow keys across cells + Enter/↓
// to add a row) and the canonical blue focus accent.
// Only refs + manual fields are saved; ident/name/ЕМ/prices are derived from items.

export interface RazmerRow {
  phaseType: string;
  priority: string;
  itemId: string;
  ident: string;
  name: string;
  requiredQty: string;
  unitCode: string;
  // Derived, second view of requiredQty NORMALIZED TO KILOGRAMS — never persisted,
  // always recomputed from the row's OWN unitCode via KG_FACTOR_BY_UNIT. Only
  // meaningful (rendered/editable/totaled) for rows in the Припрема group — see
  // isPripremaRow below. See computeSecondaryQty/computeRequiredQtyFromSecondary
  // for the two-way conversion.
  secondaryQty: string;
  // Weighing tolerance — auto-filled (snapshot) from the selected item's
  // material-type classification when the item is picked; not user-typed.
  toleranceLower: string;
  toleranceUpper: string;
  allowedWaste: string;
  allowedYield: string;
  allowedRangeManual: string;
  sieve: string;
  valuationPrice: string;
  lastPurchasePrice: string;
  // Which Subprocess this weighing row belongs to — picked per row in the first
  // column. Размерување itself stays a process-level stage (one table, all rows),
  // so this is a tag on the row, not a filter on the table.
  subprocessId: string;
  // RN actuals — optional on OS rows. `osRowId` is the Ingredients.razmer[] subdoc
  // _id and is the ONLY legal razmerActuals.rowKey (must never carry ident/qty).
  osRowId?: string;
  // Split weighing rows (one lot per row) keep their own osRowId and point back to the
  // OS Razmer row here; unsplit rows leave it empty and use osRowId as the source.
  sourceRowKey?: string;
  measuredQty?: string;
  serialNumber?: string;
  expiryDate?: string;
  // Required when the operator picks a lot other than the FEFO one.
  waste?: string;
  rastur?: string;
  workedByName?: string;
  workedAt?: string;
  workedBy?: string | { _id?: string };
  verifiedByName?: string;
  verifiedAt?: string;
  verifiedBy?: string | { _id?: string };
  verifyRequested?: boolean;
  isPreviewActual?: boolean;
  // Operator has ticked the ✓ column for this row — a conscious, one-by-one confirm
  // while standing at the scale. Two states only: empty (measuredQty "" and confirmed
  // false) or confirmed (measuredQty set and confirmed true) — the ✓ checkbox is the
  // ONLY control that moves a row between them. Ticking an empty row copies
  // `requiredQty` into `measuredQty`; ticking a row with a manually typed `measuredQty`
  // confirms it as-is. Unticking clears both back to empty. Editing measuredQty
  // clears `confirmed` back to false — a GMP rule, not a convenience; the operator
  // must consciously re-confirm after any change to the reading.
  confirmed?: boolean;
}

export const emptyRazmerRow = (): RazmerRow => ({
  phaseType: "", priority: "", itemId: "", ident: "", name: "", requiredQty: "",
  unitCode: "", secondaryQty: "", toleranceLower: "", toleranceUpper: "", allowedWaste: "", allowedYield: "", allowedRangeManual: "", sieve: "",
  valuationPrice: "", lastPurchasePrice: "", subprocessId: "",
  osRowId: "", sourceRowKey: "", measuredQty: "", serialNumber: "", expiryDate: "", waste: "", rastur: "",
  workedByName: "", workedAt: "", verifiedByName: "", verifiedAt: "", confirmed: false, isPreviewActual: false,
});

/**
 * Does this row carry anything worth keeping? Drives three things that MUST agree:
 * row creation on Enter/↓, the save filter in OS.tsx, and the mandatory-Подпроцес check.
 * `subprocessId` is deliberately NOT part of it — a new row inherits the previous row's
 * subprocess, and that alone must not make an otherwise blank trailing row look like data.
 */
export const razmerRowHasData = (r: RazmerRow): boolean =>
  !!(r.phaseType || r.itemId || r.priority || r.requiredQty || r.allowedWaste || r.allowedYield || r.allowedRangeManual || r.sieve);

/** A row with content but no stage — invalid, and blocked at save. */
export const razmerRowMissingSubprocess = (r: RazmerRow): boolean => razmerRowHasData(r) && !r.subprocessId;

interface Props {
  rows: RazmerRow[];
  onChange: (rows: RazmerRow[]) => void;
  onChangeAndSave?: (rows: RazmerRow[]) => void;
  onPreviewChange?: (rows: RazmerRow[]) => void;
  leadingActions?: React.ReactNode;
  hideCopyButton?: boolean;
  /** OS = editable recipe. RN = read-only recipe + editable actuals. */
  mode?: "os" | "rn";
  isSaving?: boolean;
  /** RN only: read-only FEFO/FIFO lot suggestion by osRowId (display only, never saved). */
  lotSuggestions?: RNRazmerLotMap;
  /**
   * RN-PROST (/work-order/6010) only: keys of columns the user hid via the Column
   * Manager (see RN_SIMPLE_RAZMER_COLUMNS below). Empty/undefined leaves every
   * column visible — identical to today's /os and /work-order/6020 rendering.
   */
  hiddenColumns?: string[];
  /** RN-PROST only: renders the floating Column Manager panel for this table. */
  enableColumnManager?: boolean;
  onColumnToggle?: (columnKey: string) => void;
  onColumnDrop?: (columnKey: string) => void;
  /**
   * RN only: autosave feedback per cell (`osRowId:field`, see useRNRazmerAutosave) and the
   * blur hook that triggers it. The table only renders the mark and reports the blur —
   * when to save, queueing and the save itself live in the hook on the screen.
   */
  cellSaveStatus?: RazmerCellSaveStatusMap;
  onActualCommit?: (rowIndex: number, field: RazmerAutosaveField) => void;
}

// Hideable Размерување columns in RN mode, in header order. The row-number column
// (leftmost, no header label in RN) is intentionally excluded — it must always stay
// visible, same as a row-select checkbox elsewhere. Keys mirror the RazmerRow fields
// they render so `hiddenColumns` can be shared verbatim with save/other logic if ever
// needed; labelKeys point at locales/{en,mk}/translation.json → rnSimpleRazmerColumns.
export const RN_SIMPLE_RAZMER_COLUMNS: ColumnDef[] = [
  { key: "phaseType", labelKey: "rnSimpleRazmerColumns.phaseType" },
  { key: "priority", labelKey: "rnSimpleRazmerColumns.priority" },
  { key: "ident", labelKey: "rnSimpleRazmerColumns.ident" },
  { key: "name", labelKey: "rnSimpleRazmerColumns.name" },
  { key: "requiredQty", labelKey: "rnSimpleRazmerColumns.requiredQty" },
  { key: "unitCode", labelKey: "rnSimpleRazmerColumns.unitCode" },
  { key: "sieve", labelKey: "rnSimpleRazmerColumns.sieve" },
  { key: "measuredQty", labelKey: "rnSimpleRazmerColumns.measuredQty" },
  { key: "confirmed", labelKey: "rnSimpleRazmerColumns.confirmed" },
  { key: "serialNumber", labelKey: "rnSimpleRazmerColumns.serialNumber" },
  { key: "expiryDate", labelKey: "rnSimpleRazmerColumns.expiryDate" },
  { key: "waste", labelKey: "rnSimpleRazmerColumns.waste" },
  { key: "rastur", labelKey: "rnSimpleRazmerColumns.rastur" },
  { key: "workedBy", labelKey: "rnSimpleRazmerColumns.workedBy" },
  { key: "verifiedBy", labelKey: "rnSimpleRazmerColumns.verifiedBy" },
];
const RN_SIMPLE_RAZMER_COLUMN_KEYS = RN_SIMPLE_RAZMER_COLUMNS.map((c) => c.key);

const formatNum = (n: any): string => {
  const v = parseDecimalInput(n);
  if (v === null || v === 0) return "";
  return formatUnitPrice(v);
};

const formatQtyNum = (n: any): string => formatQtyForDisplay(parseDecimalInput(n));

const unitCodeOf = (item: any): string => {
  const u = item.mainUnit || item.summaryUnit || item.unitMeasurment || item.unit;
  if (!u) return "";
  return typeof u === "object" ? (u.code || u.unitMeasurment || u.name || "") : "";
};

const entityIdOf = (value?: string | { _id?: string } | null): string => {
  if (!value) return "";
  return typeof value === "object" ? (value._id || "") : value;
};


// Внес од корисник — „1,5" е еден и пол. Порано овде се бришеа запирките, што го
// претвораше во 15. Види `utils/decimalInput.ts`.
const parseNum = parseDecimalInput;

// Two-way conversion between Потребна количина (main UoM) and Кол. во СП ЕМ
// (secondary UoM). Same direction as DocumentSpreadsheetBody.tsx:961-965
// (quantity = qtySecondary / conversionFactor) — do not flip it.
// Returns null (never write NaN/Infinity) when the factor is missing/0 or the
// input doesn't parse; callers must skip patching the other field in that case.
// Количините НЕ смеат да се форматираат со `formatNum`. Тој групира илјади со запирка
// („1,000.00"), а обете полиња подолу се уредливи, па нивната содржина се враќа низ
// `parseDecimalInput` — кој по МК конвенција ја чита запирката како ДЕЦИМАЛНА. „1,000"
// би се прочитало како 1, а не 1000, и количината тивко би паднала (види
// `utils/decimalInput.ts`). Затоа овде: без групирање, без принудни децимали, а децималниот
// сепаратор е запирка (МК конвенција) — исто како `parseDecimalInput` што ја чита назад.
// Само ЦЕНИТЕ одат низ formatNum — таму групирањето и двете децимали се точни.
// Дозволеното отстапување е количина и оди низ formatQtyNum (запирка, без групирање).
const formatQty = (n: number): string => formatQtyForDisplay(n);

// Кол во KG: секое ЕМ на редот НОРМАЛИЗИРАНО во килограми. Факторот е "колку kg е 1
// единица" — kg/l остануваат непроменети (фактор 1), g/ml се делат со 1000.
// l и ml → kg претпоставуваат густина 1 g/ml (вода-слична густина): Item схемата
// НЕМА поле за густина (проверено — само netWeight/grossWeight, кои се нешто друго),
// па ова е НАМЕРНА поедноставувачка претпоставка, не пропуст. Секое друго ЕМ (бр, m,
// m2, празно, непознато) НЕ е конвертибилно — намерно нема default, никогаш не се
// погодува.
const KG_FACTOR_BY_UNIT: Record<string, number> = {
  kg: 1,
  g: 0.001,
  ml: 0.001,
  l: 1,
};

export const kgFactorOf = (unitCode: string): number | null => {
  const key = (unitCode || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(KG_FACTOR_BY_UNIT, key) ? KG_FACTOR_BY_UNIT[key] : null;
};

export const computeSecondaryQty = (requiredQty: string, unitCode: string): string | null => {
  const factor = kgFactorOf(unitCode);
  if (factor == null) return null;
  const qty = parseNum(requiredQty);
  if (qty == null) return null;
  return formatQty(qty * factor);
};

const computeRequiredQtyFromSecondary = (secondaryQty: string, unitCode: string): string | null => {
  const factor = kgFactorOf(unitCode);
  if (factor == null) return null;
  const qty = parseNum(secondaryQty);
  if (qty == null) return null;
  return formatQty(qty / factor);
};

// Computes "Дозволено отстапување (ЕМ)" as grams/units from the selected item's
// Classification allowedWaste percentage: requiredQty is 100%, so allowedWaste%
// is requiredQty * allowedWaste / 100.
/**
 * Бројчаната вредност на „Дозволено отстапување (ЕМ)" за зачувување.
 *
 * `allowedRangeOf` враќа ДВА извора на стринг: пресметан преку `formatQtyNum`, или СУРОВ
 * внес од корисник кога има рачен override. Порано пресметаниот беше `en-US` (запирка =
 * илјади) а рачниот МК (запирка = децимала), па парсирањето на двата исто беше 10× грешка:
 * рачно напишано „1,5" влегуваше во базата како 15. Сега ДВАТА се МК без групирање на
 * илјади, па `parseDecimalInput` е точен за обете гранки и нема двосмисленост.
 */
export const allowedRangeNumberOf = (row: RazmerRow): number | null =>
  row.allowedRangeManual.trim()
    ? parseDecimalInput(row.allowedRangeManual)
    : parseDecimalInput(allowedRangeOf(row));

export const allowedRangeOf = (row: RazmerRow): string => {
  if (row.allowedRangeManual.trim()) return row.allowedRangeManual;
  const qty = parseNum(row.requiredQty);
  const waste = parseNum(row.allowedWaste);
  if (qty == null || waste == null) return row.allowedYield || "";
  return formatQtyNum(qty * waste / 100);
};

// `dragKey` mirrors TableSortTh's prop of the same name (RN-PROST Column Manager
// only — see that component's comment). Omitted everywhere else, so /os and
// /work-order/6020 render this exact `th` unchanged.
// `fixed`: the column keeps exactly `w` (short values like Сито / Отпад) instead of absorbing spare width.
const TH: React.FC<{ w: number; children: React.ReactNode; dragKey?: string; fixed?: boolean }> = ({ w, children, dragKey, fixed }) => (
  <th
    className={`border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[26px] px-1.5${dragKey ? " cursor-grab" : ""}`}
    style={fixed ? { minWidth: w, width: w } : { minWidth: w }}
    draggable={!!dragKey}
    onDragStart={dragKey ? (e) => {
      e.dataTransfer.setData("text/plain", dragKey);
      e.dataTransfer.effectAllowed = "move";
    } : undefined}
  >
    {children}
  </th>
);

// Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant this file
// used to carry is the known accent bug, not a style choice).
const cellBase = `w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;
const selectCls = `w-full h-6 px-0.5 text-[11px] border-0 outline-none bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_SELECT_CLS}`;
// Read-only = same look as a locked normativ cell (white, normal text), not a grey disabled block.
const readOnlyCellBase = "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-gray-800 cursor-default";
const readOnlySelectCls = "w-full h-6 px-0.5 text-[11px] border-0 outline-none bg-transparent text-gray-800 cursor-default appearance-none";

const formatDate = (value?: string): string => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

const OSRazmerTable: React.FC<Props> = ({
  rows, onChange, onChangeAndSave, onPreviewChange, leadingActions, hideCopyButton = false, mode = "os", isSaving, lotSuggestions,
  hiddenColumns = [], enableColumnManager = false, onColumnToggle, onColumnDrop, cellSaveStatus, onActualCommit,
}) => {
  const isRn = mode === "rn";
  // Autosave mark + blur report for Кол. раз./Отпад/Растур; `pr-4` keeps the digits clear
  // of the mark while it is shown (the columns are fixed-width, the mark overlays).
  const actualCellSaveStatus = (row: RazmerRow, field: RazmerAutosaveField) => (
    cellSaveStatus?.[razmerAutosaveCellKey(row, field)]
  );
  const actualCellMarkCls = (row: RazmerRow, field: RazmerAutosaveField) => (
    actualCellSaveStatus(row, field) ? " pr-4" : ""
  );
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { getItem, fetchItemById } = useItemCache();
  const phaseTypeOptions = usePhaseTypeOptions();
  const subprocesses = useSubprocesses();
  const inputRefs = useRef<(HTMLInputElement | HTMLSelectElement | null)[][]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { user } = useContext(AuthContext);
  const currentUserId = user?._id || "";

  // This table is controlled: every edit rebuilds the array from the `rows` PROP. A handler
  // that closes over `rows` therefore holds whatever the array looked like when it was created
  // — fine for a synchronous keystroke, wrong for `selectMain`'s background item fetch, whose
  // `.then` can land several edits later and would write a stale array back to the parent,
  // wiping everything typed in the meantime. Always rebuild from the latest committed rows.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const patch = (i: number, p: Partial<RazmerRow>) => {
    if (isRn) return;
    onChange(rowsRef.current.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  };

  const patchActual = (i: number, p: Partial<RazmerRow>) => {
    if (!isRn) return;
    // Секоја измена го брише авторот — серверот го препишува при Зачувај. Редоследот е
    // намерен: дефолт-бришењето оди ПРЕД ...p, за повикувач (потврда на предлог / рачна
    // корекција) да може ЕКСПЛИЦИТНО да прикачи оптимистички workedByName/workedAt во p
    // и тоа да победи. Никогаш обратно — иначе секое трепкање on-change би ја избришало
    // оптимистичката вредност истиот момент кога е поставена.
    onChange(rowsRef.current.map((r, idx) => (idx === i ? {
      ...r,
      workedByName: "",
      workedAt: "",
      verifiedByName: "",
      verifiedAt: "",
      verifyRequested: false,
      isPreviewActual: false,
      ...p,
    } : r)));
  };

  const setVerifyRequested = (i: number, verifyRequested: boolean) => {
    if (!isRn) return;
    const updated = rowsRef.current.map((r, idx) => (idx === i ? { ...r, verifyRequested } : r));
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  };

  // The second user can verify every already-saved, confirmed row in one action.
  // Keep the eligibility identical to the per-row "Проверил" checkbox so a bulk
  // action can never bypass the two-person audit rule.
  const rnVerifiableRows = useMemo(() => {
    if (!isRn) return [];
    return rows.filter((row) => {
      const workerId = entityIdOf(row.workedBy);
      return !!(
        row.confirmed
        && workerId
        && workerId !== currentUserId
        && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)
      );
    });
  }, [rows, isRn, currentUserId]);
  const allRnVerifiedOrRequested = rnVerifiableRows.length > 0
    && rnVerifiableRows.every((row) => !!(row.verifiedByName || row.verifiedAt || row.verifyRequested));
  const someRnVerifiedOrRequested = rnVerifiableRows.some(
    (row) => !!(row.verifiedByName || row.verifiedAt || row.verifyRequested)
  );
  const hasOpenRnVerification = rnVerifiableRows.some((row) => !(row.verifiedByName || row.verifiedAt));

  const toggleAllVerification = () => {
    if (!isRn || isSorted || isSaving || !hasOpenRnVerification) return;
    const shouldRequest = !allRnVerifiedOrRequested;
    const updated = rowsRef.current.map((row) => {
      const workerId = entityIdOf(row.workedBy);
      const canVerify = !!(
        row.confirmed
        && workerId
        && workerId !== currentUserId
        && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)
      );
      const isVerified = !!(row.verifiedByName || row.verifiedAt);
      return canVerify && !isVerified ? { ...row, verifyRequested: shouldRequest } : row;
    });
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  };

  // ─── Confirm all (RN only) — master ✓ in the column header ────────────────────────────
  // "Eligible" mirrors the per-row checkbox's disabled rule: a row with neither a
  // planned nor a measured quantity has nothing to confirm and nothing to copy.
  const rnEligibleRows = useMemo(() => {
    if (!isRn) return [];
    return rows.filter((r) => !!(r.requiredQty || "").trim() || !!(r.measuredQty || "").trim());
  }, [rows, isRn]);

  const allRnConfirmed = rnEligibleRows.length > 0 && rnEligibleRows.every((r) => r.confirmed);
  const someRnConfirmed = rnEligibleRows.some((r) => r.confirmed);
  const hasCopyableRequiredQty = isRn && rows.some((r) => !!(r.requiredQty || "").trim() && !(r.measuredQty || "").trim());

  const copyRequiredQtyToMeasured = () => {
    if (!isRn || isSorted || isSaving) return;
    const updated = rowsRef.current.map((r) => {
      if (!(r.requiredQty || "").trim() || (r.measuredQty || "").trim()) return r;
      return {
        ...r,
        measuredQty: r.requiredQty,
        confirmed: false,
        workedByName: "",
        workedAt: "",
        verifiedByName: "",
        verifiedAt: "",
        verifyRequested: false,
        isPreviewActual: true,
      };
    });
    if (onPreviewChange) onPreviewChange(updated);
    else onChange(updated);
  };

  const toggleAllConfirmed = () => {
    if (!isRn || isSorted) return;
    // Built as ONE array + ONE onChange — patchActual (one row at a time, closing over
    // rowsRef.current) would lose all but the last change if looped synchronously,
    // since rowsRef.current only refreshes on the next render.
    const shouldConfirm = !allRnConfirmed;
    const updated = rowsRef.current.map((r) => {
      const eligible = !!(r.requiredQty || "").trim() || !!(r.measuredQty || "").trim();
      if (!eligible) return r;
      if (shouldConfirm) {
        // Already-confirmed rows are untouched — a second person's Проверил (verified)
        // signature must never be blanked by ticking the master box for the ONE row
        // that's still unconfirmed.
        if (r.confirmed) return r;
        const hasTyped = !!(r.measuredQty || "").trim();
        return {
          ...r,
          measuredQty: hasTyped ? r.measuredQty : r.requiredQty,
          confirmed: true,
          isPreviewActual: false,
          workedByName: "",
          workedAt: "",
          verifiedByName: "",
          verifiedAt: "",
          verifyRequested: false,
        };
      }
      return {
        ...r,
        measuredQty: "",
        confirmed: false,
        isPreviewActual: false,
        workedByName: "",
        workedAt: "",
        verifiedByName: "",
        verifiedAt: "",
        verifyRequested: false,
      };
    });
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  };

  const confirmActualRow = (index: number) => {
    const result = prepareRNRazmerLotConfirmation(rowsRef.current[index], lotSuggestions?.[rowsRef.current[index].osRowId || ""]);
    if ("error" in result) {
      const messageKey = result.error === "shortfall" ? "insufficientStock" : result.error === "invalidLot" ? "invalidLot" : "previewPending";
      showToast(result.message || t(`rnRazmerLots.${messageKey}`), "error");
      return;
    }
    const updated = rowsRef.current.flatMap((current, currentIndex) => currentIndex === index ? result.rows : [current]);
    if (onChangeAndSave) onChangeAndSave(updated); else onChange(updated);
  };
  const setRows = (u: React.SetStateAction<RazmerRow[]>) => {
    if (isRn) return;
    onChange(typeof u === "function" ? (u as (p: RazmerRow[]) => RazmerRow[])(rowsRef.current) : u);
  };
  // Подпроцесот е задолжителен, па новиот ред го наследува од претходниот: пишување во една
  // фаза е нормалниот тек, а инаку секој ред бара уште еден избор. useSpreadsheetKeyboard
  // додава на КРАЈОТ на низата, така што последниот ред е точниот извор за наследување.
  // (Споделениот хук не се менува — createEmptyRow е обичен callback и тука се затвора над
  // rowsRef, не над `rows`, за да не наследи од застарена низа.)
  const createEmptyRowInheritingStage = (): RazmerRow => {
    const last = rowsRef.current[rowsRef.current.length - 1];
    return { ...emptyRazmerRow(), subprocessId: last?.subprocessId || "" };
  };

  // ─── Group rows by MAIN PROCESS (subprocess.processDefinition) ─────────────
  // Rows carry `subprocessId`; the main process is that subprocess's populated
  // `processDefinition`. Groups are derived from the data (never hardcoded to
  // "Припрема"/"Пакување") and ordered by processDefinition.order, falling back
  // to name — so a third process slots in on its own. Rows with a missing/
  // unresolved processDefinition rank last ("__unknown__").
  //
  // CRITICAL: useSpreadsheetKeyboard + inputRefs address rows by their INDEX IN
  // THE `rows` ARRAY. OS applies grouping to that array via onChange. RN only
  // reorders displayItems, which preserve each row's original index for
  // Tab/arrows/Delete and other index-addressed handlers.
  const subprocessesById = useMemo(() => new Map(subprocesses.map((sp) => [sp._id, sp])), [subprocesses]);
  const processGroupOrder = useMemo(() => buildProcessGroupOrder(subprocesses), [subprocesses]);

  const processGroupKey = (row: RazmerRow): string => {
    return processGroupIdForSubprocess(row.subprocessId, subprocessesById);
  };

  const processGroupRank = (row: RazmerRow): number => {
    return getProcessGroupRank(processGroupKey(row), processGroupOrder);
  };

  // „Кол во KG" важи САМО за Припрема. Не е хардкодиран литерал — Припрема е
  // резолвирана како ПРВАТА група во веќе изведениот `processGroupOrder` (истиот
  // редослед што веќе го одредува сортирањето и делителот меѓу групите погоре), па
  // ако некогаш се смени множеството процеси, оваа функција останува точна без
  // измена. Единствено место за менување ако тоа некогаш престане да важи.
  const isPripremaRow = (row: RazmerRow): boolean =>
    processGroupOrder.length > 0 && processGroupRank(row) === 0;

  // Вкупно kg — сума на „Кол во KG" на конвертибилните Припрема редови (истите
  // редови што ја имаат ќелијата уредлива, kgEditable). row.secondaryQty е веќе
  // синхронизирано со requiredQty+unitCode низ populateFromItem/onChange, па нема
  // потреба повторно да се пресметува тука. Празно, никогаш „0", кога нема ниту еден.
  const pripremaKgTotal = useMemo(() => {
    let sum = 0;
    let any = false;
    for (const row of rows) {
      if (!isPripremaRow(row) || kgFactorOf(row.unitCode) == null) continue;
      const v = parseNum(row.secondaryQty);
      if (v == null) continue;
      sum += v;
      any = true;
    }
    return any ? formatQty(sum) : "";
  }, [rows, processGroupOrder, subprocesses]);

  // Re-sort the underlying array (not the render) whenever rows arrive/change
  // from the parent (normativ loaded) or a row's subprocessId changes — both
  // surface as a new `rows` prop, so one effect on [rows, subprocesses] covers
  // both triggers. Array.prototype.sort is stable (ES2019+), so rows already
  // inside a group keep their existing relative order. The `changed` identity
  // check (same references in the same order) is what stops an
  // onChange → re-render → effect → onChange loop: once sorted, sorting again
  // produces an identical array and no onChange fires.
  useEffect(() => {
    if (isRn) return;
    if (subprocesses.length === 0) return;
    const sorted = [...rows].sort((a, b) => processGroupRank(a) - processGroupRank(b));
    const changed = sorted.some((r, idx) => r !== rows[idx]);
    if (changed) {
      // Selection + input refs are position-indexed; a reorder moves which
      // row lives at a given position, so stale selection could delete the
      // wrong row. Clear both, same as deleteSelected does.
      setSelected(new Set());
      inputRefs.current = [];
      onChange(sorted);
    }
  }, [isRn, rows, subprocesses]);

  const { handleKeyDown } = useSpreadsheetKeyboard({
    rows,
    setRows,
    inputRefs,
    createEmptyRow: createEmptyRowInheritingStage,
    hasRowData: razmerRowHasData,
  });

  const reg = (i: number, col: number) => ({
    ref: (el: HTMLInputElement | HTMLSelectElement | null) => {
      if (!inputRefs.current[i]) inputRefs.current[i] = [];
      inputRefs.current[i][col] = el;
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => handleKeyDown(e, i, col),
  });

  // ─── View sort (presentation only — never persisted, never reorders `rows`) ────────────
  const getRazmerValue = useCallback((row: RazmerRow, field: string): string | number => {
    switch (field) {
      case "subprocess":
        return subprocesses.find((s) => s._id === row.subprocessId)?.name || "";
      case "phaseType":
        return phaseTypeOptions.find((p) => p._id === row.phaseType)?.name || "";
      case "priority":
        return parseNum(row.priority) ?? 0;
      case "ident":
        return row.ident || "";
      case "name":
        return row.name || "";
      case "requiredQty":
        return parseNum(row.requiredQty) ?? 0;
      case "unitCode":
        return row.unitCode || "";
      default:
        return "";
    }
  }, [subprocesses, phaseTypeOptions]);

  const { sortField, sortDirection, handleSort, resetSort, isSorted, sortedItems } =
    useTableViewSort<RazmerRow>(rows, getRazmerValue);

  // RN is grouped only for display. Every item retains its original `index`, so
  // row mutations, focus refs, search, and persisted actuals still address `rows`.
  // Split lot rows inherit their source row's position and priority, keeping them
  // directly after that source regardless of their own original position.
  const displayItems = useMemo(() => {
    if (!isRn || isSorted) return sortedItems;
    const sourceMeta = new Map<string, { priority: number; index: number; isSource: boolean }>();
    sortedItems.forEach(({ item, index }) => {
      const sourceKey = item.sourceRowKey || item.osRowId || `row:${index}`;
      const previous = sourceMeta.get(sourceKey);
      const isSource = item.osRowId === sourceKey;
      if (!previous || (isSource && !previous.isSource) || (isSource === previous.isSource && index < previous.index)) {
        sourceMeta.set(sourceKey, { priority: parseNum(item.priority) ?? 0, index, isSource });
      }
    });
    return [...sortedItems].sort((a, b) => {
      const groupDifference = processGroupRank(a.item) - processGroupRank(b.item);
      if (groupDifference) return groupDifference;
      const aSourceKey = a.item.sourceRowKey || a.item.osRowId || `row:${a.index}`;
      const bSourceKey = b.item.sourceRowKey || b.item.osRowId || `row:${b.index}`;
      const aSource = sourceMeta.get(aSourceKey)!;
      const bSource = sourceMeta.get(bSourceKey)!;
      if (aSource.priority !== bSource.priority) return aSource.priority - bSource.priority;
      if (aSource.index !== bSource.index) return aSource.index - bSource.index;
      if (aSourceKey === bSourceKey) {
        const aIsSource = a.item.osRowId === aSourceKey;
        const bIsSource = b.item.osRowId === bSourceKey;
        if (aIsSource !== bIsSource) return aIsSource ? -1 : 1;
      }
      return a.index - b.index;
    });
  }, [isRn, isSorted, sortedItems, processGroupOrder, subprocessesById]);

  // Display index of the last Припрема row — the total row is rendered right after it,
  // which puts it immediately above the double-line process divider. -1 when the table
  // has no Припрема rows.
  const lastPripremaDisplayIdx = useMemo(() => {
    let last = -1;
    displayItems.forEach(({ item }, displayIdx) => {
      if (isPripremaRow(item)) last = displayIdx;
    });
    return last;
  }, [displayItems, processGroupOrder, subprocesses]);

  // ─── Search (marks + jumps — never filters, never reorders) ────────────────────────────
  const searchCells = useMemo<SearchCell[]>(() => {
    const cells: SearchCell[] = [];
    displayItems.forEach(({ item: row, index }) => {
      if (!isRn) {
        const spName = subprocesses.find((s) => s._id === row.subprocessId)?.name;
        if (spName) cells.push({ id: `${index}:subprocess`, text: spName });
      }
      const ptName = phaseTypeOptions.find((p) => p._id === row.phaseType)?.name;
      if (ptName) cells.push({ id: `${index}:phaseType`, text: ptName });
      if (row.ident) cells.push({ id: `${index}:ident`, text: row.ident });
      if (row.name) cells.push({ id: `${index}:name`, text: row.name });
      if (row.unitCode) cells.push({ id: `${index}:unitCode`, text: row.unitCode });
      if (row.sieve) cells.push({ id: `${index}:sieve`, text: row.sieve });
    });
    return cells;
  }, [displayItems, subprocesses, phaseTypeOptions, isRn]);

  const {
    searchInput, setSearchInput, searchTerm, handleSearch, clearSearch,
    matchCount, activeIndex, next, prev, registerCell, isMatch, isActive,
  } = useTableSearchHighlight(searchCells);

  const cellHighlightCls = (id: string): string => {
    if (isActive(id)) return " bg-blue-300 dark:bg-blue-700/70 ring-2 ring-blue-500";
    if (isMatch(id)) return " bg-blue-100 dark:bg-blue-900/40";
    return "";
  };

  // Sorting the VIEW locks editing in this table — index-addressed keyboard nav/selection
  // must never fire while displayed order differs from the `rows` array order.
  const locked = isRn || isSorted;

  // Resolves the old weighing tolerance snapshot from material-type classifications.
  // Kept for existing persisted data, but "Дозволено отстапување (ЕМ)" now uses allowedWaste.
  const resolveTolerance = (item: any): { toleranceLower: string; toleranceUpper: string } => {
    for (const c of [item?.rawMaterialType, item?.paType, item?.saType]) {
      if (c && typeof c === "object" && (c.toleranceLower != null || c.toleranceUpper != null)) {
        return {
          toleranceLower: c.toleranceLower != null ? String(c.toleranceLower) : "",
          toleranceUpper: c.toleranceUpper != null ? String(c.toleranceUpper) : "",
        };
      }
    }
    return { toleranceLower: "", toleranceUpper: "" };
  };

  // ИСТИОТ ланец како resolveTolerance погоре, и намерно: кај суровините отпадот и
  // отстапувањата доаѓаат од една иста материјална класа. Порано ова читаше само
  // `tercialClass`, кој суровините (книг. 5xx) воопшто го немаат — па колоната „Дозволен
  // отпад (%)", а со неа и „Дозволено отстапување (ЕМ)", остануваа празни секогаш.
  // `tercialClass` останува последен во ланецот заради готовите производи.
  // Огледало на resolveAllowedWasteFromItem во server/routes/ingredients.js.
  const resolveAllowedWaste = (item: any): string => {
    for (const c of [item?.rawMaterialType, item?.paType, item?.saType, item?.tercialClass]) {
      if (c && typeof c === "object" && c.allowedWaste != null) {
        return String(c.allowedWaste);
      }
    }
    return "";
  };

  const populateFromItem = (item: any, currentRequiredQty: string): Partial<RazmerRow> => {
    const unitCode = unitCodeOf(item);
    return {
      ident: item.ident || "",
      name: item.name || "",
      unitCode,
      secondaryQty: computeSecondaryQty(currentRequiredQty, unitCode) ?? "",
      valuationPrice: formatNum(item.valuationPrice),
      lastPurchasePrice: formatNum(item.purchasePrice),
      allowedWaste: resolveAllowedWaste(item),
      allowedYield: "",
      ...resolveTolerance(item),
    };
  };

  const selectMain = (i: number, id: string) => {
    if (!id) {
      patch(i, { itemId: "", ident: "", name: "", unitCode: "", secondaryQty: "", valuationPrice: "", lastPurchasePrice: "", toleranceLower: "", toleranceUpper: "", allowedWaste: "", allowedYield: "", allowedRangeManual: "" });
      return;
    }
    // First try cache (instant) - items are already cached from dropdown search
    const item = getItem(id);
    if (item) {
      patch(i, { itemId: id, ...populateFromItem(item, rowsRef.current[i]?.requiredQty || "") });
    } else {
      // Fetch from API in background if not in cache
      patch(i, { itemId: id });
      fetchItemById(id).then((fetchedItem) => {
        if (fetchedItem) {
          patch(i, { itemId: id, ...populateFromItem(fetchedItem, rowsRef.current[i]?.requiredQty || "") });
        }
      }).catch(() => {
        // Item fetch failed silently - non-critical
      });
    }
  };

  const isAllSelected = rows.length > 0 && selected.size === rows.length;
  const isSomeSelected = selected.size > 0;

  const toggleAll = () => {
    if (isSorted) return;
    setSelected(isAllSelected ? new Set() : new Set(rows.map((_, i) => i)));
  };

  const toggleRow = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const deleteSelected = () => {
    if (isRn || isSorted) return;
    const kept = rows.filter((_, i) => !selected.has(i));
    onChange(kept.length === 0 ? [emptyRazmerRow()] : kept);
    setSelected(new Set());
    inputRefs.current = [];
  };

  // On RN the "plan" (ОС/норматив) columns are read-only carryover from the recipe. Since
  // 2026-10-09 they look like a locked normativ cell (white, lighter text) instead of a dark
  // grey block; /os keeps readOnlyCellBase/readOnlySelectCls.
  const planReadOnlyCellBase = isRn
    ? "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-gray-600 cursor-default"
    : readOnlyCellBase;
  const planReadOnlySelectCls = isRn
    ? "w-full h-6 px-0.5 text-[11px] border-0 outline-none bg-transparent text-gray-600 cursor-default appearance-none"
    : readOnlySelectCls;
  const planCellCls = isRn ? " bg-slate-50 cursor-default" : ""; // light tint = pulled from the normativ (2026-10-09), not a dark disabled block
  const planInputCls = locked ? planReadOnlyCellBase : cellBase;
  const planSelectCls = locked ? planReadOnlySelectCls : selectCls;

  // Column count for the process-divider row's colSpan — must track the <th> list in
  // the <thead> below exactly, per mode. Counted by hand against that JSX:
  // OS (15): checkbox, trash, Подпроцес, Тип на фаза, Фаза, Шифра, Назив, Потребна
  //   количина, ЕМ, Кол во KG, Дозволено отстапување (ЕМ), Дозволен отпад (%), Димензија на
  //   сито (mm), Цена за вреднување, Последна набавна цена.
  // RN (16): trash, Тип на фаза, Фаза, Шифра, Назив, Потребна количина, ЕМ,
  //   Димензија на сито (mm), Количина размерена, ✓, Сериски број, Рок, Отпад,
  //   Растур, Работел, Проверил.
  // RN-PROST (6010) can hide any of the 15 RN columns above (minus the leading
  // row-number column) via the Column Manager — subtract those from the hand count
  // so the divider row's colSpan keeps tracking what's actually rendered.
  const hiddenRnColumnCount = isRn
    ? hiddenColumns.filter((key) => RN_SIMPLE_RAZMER_COLUMN_KEYS.includes(key)).length
    : 0;
  const totalCols = (isRn ? 16 : 15) - hiddenRnColumnCount;
  const isColHidden = (key: string): boolean => hiddenColumns.includes(key);

  // RN-PROST Column Manager: drop a hidden column's header back onto the table to
  // restore it. Mirrors ColumnManager.tsx's own drop zone (`text/plain` payload).
  // A visible header dropped here is a no-op — `handleColumnToggle` only ever hides
  // (see useRazmerHiddenColumns.ts), so re-toggling an already-visible key would hide it.
  const handleTheadDrop = (e: React.DragEvent<HTMLTableSectionElement>) => {
    e.preventDefault();
    const key = e.dataTransfer.getData("text/plain");
    if (key && hiddenColumns.includes(key)) onColumnToggle?.(key);
  };

  // Shared markup for the "Вкупно kg" row — rendered inline right after the last
  // Припрема row (so it sits above the process divider) when the table is unsorted
  // and has Припрема rows, or as a <tfoot> fallback otherwise. One definition, two
  // placements, so they can never drift apart.
  const renderKgTotalRow = () => {
    if (isRn) return null;
    return (
    <tr>
      <td
        colSpan={isRn ? 8 : 10}
        className="border border-gray-300 bg-gray-50 h-[26px] px-1.5 py-0.5 text-right text-[11px] text-gray-600 font-medium"
      >
        Вкупно kg
      </td>
      <td className="border border-gray-300 bg-gray-50 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-800 font-bold">
        {pripremaKgTotal}
      </td>
      <td colSpan={isRn ? 7 : 5} className="border border-gray-300 bg-gray-50 h-[26px] px-1.5 py-0.5" />
    </tr>
    );
  };

  return (
    <OSCard
      title="Размерување"
      notice={isSorted && <OSCardNotice>{t("tableTools.sortLockNotice")}</OSCardNotice>}
      actions={(
        <TableViewToolbar
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          clearSearch={clearSearch}
          matchCount={matchCount}
          activeIndex={activeIndex}
          next={next}
          prev={prev}
          sortField={sortField}
          sortDirection={sortDirection}
          isSorted={isSorted}
          resetSort={resetSort}
          t={t}
          leadingActions={isRn ? (
            <>
            {leadingActions}
            {!hideCopyButton && <button
              type="button"
              onClick={copyRequiredQtyToMeasured}
              disabled={isSaving || isSorted || !hasCopyableRequiredQty}
              className={`group relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 text-[11px] font-semibold bg-white ${
                isSaving || isSorted || !hasCopyableRequiredQty
                  ? "border border-gray-300 text-gray-400 cursor-not-allowed"
                  : "border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105 focus:ring-blue-500/50"
              }`}
              title={hasCopyableRequiredQty ? "Копирај потребни количини" : "Нема количини за копирање"}
              aria-label="Копирај потребни количини"
            >
              <Copy size={14} strokeWidth={2.5} />
              Копирај
            </button>}
            </>
          ) : undefined}
        />
      )}
    >
      <div className="overflow-x-auto bg-white">
        <table className={`border-collapse w-full ${isRn ? "min-w-[1040px]" : "min-w-[1100px]"}`}>
          <thead
            onDragOver={enableColumnManager ? (e) => e.preventDefault() : undefined}
            onDrop={enableColumnManager ? handleTheadDrop : undefined}
          >
            <tr>
              {!isRn && (
                <th className="border border-gray-300 bg-gray-200 w-8 h-[26px] px-1">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    checked={isAllSelected}
                    ref={(el) => { if (el) el.indeterminate = isSomeSelected && !isAllSelected; }}
                    onChange={toggleAll}
                    disabled={isSorted}
                  />
                </th>
              )}
              <th className="border border-gray-300 bg-gray-200 w-6 h-[26px] px-1">
                {isRn ? null : (
                  <button
                    onClick={deleteSelected}
                    disabled={!isSomeSelected || isSorted}
                    className={`${isSomeSelected && !isSorted ? "text-red-500 hover:text-red-700 cursor-pointer" : "text-gray-300 cursor-not-allowed"} transition-colors`}
                    title={isSomeSelected ? `Избриши ${selected.size} избрани редови` : "Нема избрани редови"}
                  >
                    <Trash2 size={14} className="mx-auto" />
                  </button>
                )}
              </th>
              {!isRn && (
                <TableSortTh disabled w={110} field="subprocess" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>
                  Подпроцес
                </TableSortTh>
              )}
              {!isColHidden("phaseType") && (
                <TableSortTh disabled
                  w={110}
                  field="phaseType"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "phaseType" : undefined}
                >
                  Тип на фаза
                </TableSortTh>
              )}
              {!isColHidden("priority") && (
                <TableSortTh disabled
                  w={70}
                  field="priority"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "priority" : undefined}
                >
                  Фаза
                </TableSortTh>
              )}
              {!isColHidden("ident") && (
                <TableSortTh disabled
                  w={120}
                  field="ident"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "ident" : undefined}
                >
                  Шифра
                </TableSortTh>
              )}
              {!isColHidden("name") && (
                <TableSortTh disabled
                  w={isRn ? 320 : 180}
                  field="name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "name" : undefined}
                >
                  Назив
                </TableSortTh>
              )}
              {!isColHidden("requiredQty") && (
                <TableSortTh disabled
                  w={110}
                  field="requiredQty"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "requiredQty" : undefined}
                >
                  <span title="Потребна количина">Пот. кол.</span>
                </TableSortTh>
              )}
              {!isColHidden("unitCode") && (
                <TableSortTh disabled
                  w={60}
                  field="unitCode"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  dragKey={enableColumnManager ? "unitCode" : undefined}
                >
                  ЕМ
                </TableSortTh>
              )}
              {!isRn && <TH w={110}>Кол во KG</TH>}
              {!isRn && <TH w={130}>Дозволено отстапување (ЕМ)</TH>}
              {!isRn && <TH w={110}>Дозволен отпад (%)</TH>}
              {!isColHidden("sieve") && (
                <TH w={78} fixed dragKey={enableColumnManager ? "sieve" : undefined}>
                  <span title="Димензија на сито (mm)" className="whitespace-nowrap">Сито (mm)</span>
                </TH>
              )}
              {isRn ? (
                <>
                  {!isColHidden("measuredQty") && (
                    <TH w={90} fixed dragKey={enableColumnManager ? "measuredQty" : undefined}>
                      <span title="Количина размерена" className="whitespace-nowrap">Кол. раз.</span>
                    </TH>
                  )}
                  {!isColHidden("confirmed") && (
                    <TH w={34} dragKey={enableColumnManager ? "confirmed" : undefined}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        checked={allRnConfirmed}
                        ref={(el) => { if (el) el.indeterminate = someRnConfirmed && !allRnConfirmed; }}
                        onChange={toggleAllConfirmed}
                        disabled={isSorted || isSaving}
                        title="Потврди сите"
                      />
                    </TH>
                  )}
                  {!isColHidden("serialNumber") && (
                    <TH w={110} dragKey={enableColumnManager ? "serialNumber" : undefined}>
                      Сериски број
                    </TH>
                  )}
                  {!isColHidden("expiryDate") && (
                    <TH w={90} dragKey={enableColumnManager ? "expiryDate" : undefined}>
                      Рок
                    </TH>
                  )}
                  {!isColHidden("waste") && (
                    <TH w={60} fixed dragKey={enableColumnManager ? "waste" : undefined}>
                      Отпад
                    </TH>
                  )}
                  {!isColHidden("rastur") && (
                    <TH w={60} fixed dragKey={enableColumnManager ? "rastur" : undefined}>
                      Растур
                    </TH>
                  )}
                  {!isColHidden("workedBy") && (
                    <TH w={190} dragKey={enableColumnManager ? "workedBy" : undefined}>
                      Работел
                    </TH>
                  )}
                  {!isColHidden("verifiedBy") && (
                    <TH w={200} dragKey={enableColumnManager ? "verifiedBy" : undefined}>
                      <label className="flex items-center gap-1.5 cursor-pointer" title="Провери ги сите достапни редови">
                        <input
                          type="checkbox"
                          className="w-4 h-4 flex-shrink-0 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          checked={allRnVerifiedOrRequested}
                          ref={(el) => { if (el) el.indeterminate = someRnVerifiedOrRequested && !allRnVerifiedOrRequested; }}
                          onChange={toggleAllVerification}
                          disabled={isSorted || isSaving || !hasOpenRnVerification}
                        />
                        <span>Проверил</span>
                      </label>
                    </TH>
                  )}
                </>
              ) : (
                <>
                  <TH w={110}>Цена за вреднување</TH>
                  <TH w={120}>Последна набавна цена</TH>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {displayItems.map(({ item: row, index: i }, displayIdx) => {
              const prevRow = !isSorted && displayIdx > 0 ? displayItems[displayIdx - 1].item : null;
              const showDivider = !isSorted && prevRow != null && processGroupKey(prevRow) !== processGroupKey(row);
              const kgEditable = isPripremaRow(row) && kgFactorOf(row.unitCode) != null;
              const workerId = entityIdOf(row.workedBy);
              const canVerify = !!(
                row.confirmed
                && workerId
                && workerId !== currentUserId
                && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)
              );
              const isVerified = !!(row.verifiedByName || row.verifiedAt);
              return (
              <React.Fragment key={i}>
              {showDivider && (
                <tr>
                  <td colSpan={totalCols} className="h-1.5 p-0 bg-blue-100 border-x border-y border-blue-200" />
                </tr>
              )}
              <tr className="hover:bg-blue-50 focus-within:bg-blue-50">
                {!isRn && (
                  <td className="border border-gray-300 h-[26px] px-0 py-0 text-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      checked={selected.has(i)}
                      onChange={() => toggleRow(i)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isSorted}
                    />
                  </td>
                )}
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                  {isRn ? displayIdx + 1 : i + 1}
                </td>
                {!isRn && (
                  <td
                    ref={registerCell(`${i}:subprocess`)}
                    className={`border border-gray-300 h-[26px] px-0.5 py-0.5${cellHighlightCls(`${i}:subprocess`)}`}
                  >
                    <select
                      className={locked ? readOnlySelectCls : `${selectCls}${razmerRowMissingSubprocess(row) ? " ring-2 ring-red-400 bg-red-50" : ""}`}
                      value={row.subprocessId}
                      title={razmerRowMissingSubprocess(row) ? "Подпроцетот е задолжителен" : undefined}
                      onChange={(e) => patch(i, { subprocessId: e.target.value })}
                      disabled={locked}
                      {...(!locked ? reg(i, 0) : {})}
                    >
                      <option value="" disabled>Избери подпроцес</option>
                      {subprocesses.map((sp) => (
                        <option key={sp._id} value={sp._id}>
                          {sp.name}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                {!isColHidden("phaseType") && (
                  <td
                    ref={registerCell(`${i}:phaseType`)}
                    className={`border border-gray-300 h-[26px] px-0.5 py-0.5${planCellCls}${cellHighlightCls(`${i}:phaseType`)}`}
                  >
                    <select
                      className={planSelectCls}
                      value={row.phaseType}
                      onChange={(e) => patch(i, { phaseType: e.target.value })}
                      disabled={locked}
                      {...(!locked ? reg(i, 1) : {})}
                    >
                      <option value=""></option>
                      {phaseTypeOptions.map((pt) => (
                        <option key={pt._id} value={pt._id}>
                          {pt.name}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                {!isColHidden("priority") && (
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <input className={planInputCls} value={row.priority} onChange={(e) => patch(i, { priority: e.target.value })} readOnly={locked} disabled={locked} {...(!locked ? reg(i, 2) : {})} />
                  </td>
                )}
                {!isColHidden("ident") && (
                  <td
                    ref={registerCell(`${i}:ident`)}
                    className={`border border-gray-300 h-[26px] px-0.5 py-0.5${planCellCls}${cellHighlightCls(`${i}:ident`)}`}
                  >
                    <SpreadsheetItemInput
                      value={row.itemId}
                      displayValue={row.ident}
                      displayBy="code"
                      accountingTypeGroup="raw-material,packaging,semi-finished,intermediate"
                      enableValidation={true}
                      enableRef
                      onChange={(id) => selectMain(i, id)}
                      onSelect={() => inputRefs.current[i]?.[4]?.focus()}
                      disabled={locked}
                      size={isRn ? "tablePlan" : locked ? "table" : "tablePlain"}
                      {...(!locked ? reg(i, 3) : {})}
                    />
                  </td>
                )}
                {!isColHidden("name") && (
                  <td
                    ref={registerCell(`${i}:name`)}
                    className={`border border-gray-300 h-[26px] px-0.5 py-0.5${planCellCls}${cellHighlightCls(`${i}:name`)}`}
                  >
                    <SpreadsheetItemInput
                      value={row.itemId}
                      displayValue={row.name}
                      displayBy="name"
                      accountingTypeGroup="raw-material,packaging,semi-finished,intermediate"
                      enableValidation={false}
                      enableRef
                      onChange={(id) => selectMain(i, id)}
                      onSelect={() => inputRefs.current[i]?.[5]?.focus()}
                      disabled={locked}
                      size={isRn ? "tablePlan" : locked ? "table" : "tablePlain"}
                      {...(!locked ? reg(i, 4) : {})}
                    />
                  </td>
                )}
                {!isColHidden("requiredQty") && (
                  <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}`}>
                    <input
                      className={`${planInputCls} text-right tabular-nums`}
                      value={row.requiredQty}
                      readOnly={locked}
                      disabled={locked}
                      onChange={(e) => {
                        const val = e.target.value;
                        const computed = kgEditable ? computeSecondaryQty(val, row.unitCode) : null;
                        patch(i, computed != null ? { requiredQty: val, secondaryQty: computed } : { requiredQty: val });
                      }}
                      {...(!locked ? reg(i, 5) : {})}
                    />
                  </td>
                )}
                {!isColHidden("unitCode") && (
                  <td
                    ref={registerCell(`${i}:unitCode`)}
                    className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700${planCellCls}${cellHighlightCls(`${i}:unitCode`)}`}
                  >
                    {row.unitCode}
                  </td>
                )}
                {!isRn && (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    {kgEditable ? (
                      <input
                        className={locked ? `${readOnlyCellBase} text-right tabular-nums` : `${cellBase} text-right tabular-nums`}
                        value={row.secondaryQty}
                        readOnly={locked}
                        disabled={locked}
                        onChange={(e) => {
                          const val = e.target.value;
                          const computed = computeRequiredQtyFromSecondary(val, row.unitCode);
                          patch(i, computed != null ? { secondaryQty: val, requiredQty: computed } : { secondaryQty: val });
                        }}
                        {...(!locked ? reg(i, 6) : {})}
                      />
                    ) : null}
                  </td>
                )}
                {!isRn && (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    <input
                      className={locked ? `${readOnlyCellBase} text-center` : `${cellBase} text-center`}
                      value={row.allowedRangeManual}
                      placeholder={allowedRangeOf({ ...row, allowedRangeManual: "" })}
                      readOnly={locked}
                      disabled={locked}
                      onChange={(e) => patch(i, { allowedRangeManual: e.target.value })}
                      {...(!locked ? reg(i, 7) : {})}
                    />
                  </td>
                )}
                {!isRn && (
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                    {row.allowedWaste}
                  </td>
                )}
                {!isColHidden("sieve") && (
                  <td
                    ref={registerCell(`${i}:sieve`)}
                    className={`border border-gray-300 h-[26px] px-1.5 py-0.5${planCellCls}${cellHighlightCls(`${i}:sieve`)}`}
                  >
                    <input className={planInputCls} value={row.sieve} onChange={(e) => patch(i, { sieve: e.target.value })} readOnly={locked} disabled={locked} {...(!locked ? reg(i, 8) : {})} />
                  </td>
                )}
                {isRn ? (
                  <>
                    {!isColHidden("measuredQty") && (
                      <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${isSorted ? readOnlyCellBase : cellBase} text-right tabular-nums${actualCellMarkCls(row, "measuredQty")}`}
                          value={row.measuredQty || ""}
                          readOnly={isSorted || !!row.confirmed}
                          disabled={isSorted || !!row.confirmed}
                          onChange={(e) => patchActual(i, {
                            measuredQty: e.target.value,
                            confirmed: false,
                          })}
                          onBlur={() => onActualCommit?.(i, "measuredQty")}
                          {...(!(isSorted || row.confirmed) ? reg(i, 0) : {})}
                        />
                        <RNRazmerCellSaveMark status={actualCellSaveStatus(row, "measuredQty")} />
                      </td>
                    )}
                    {!isColHidden("confirmed") && (
                      <td className="border border-gray-300 h-[26px] px-1 py-0.5 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          checked={!!row.confirmed}
                          // Ништо да се потврди/копира кога редот нема ни планирана ни
                          // измерена количина.
                          disabled={isSorted || isSaving || !((row.requiredQty || "").trim() || (row.measuredQty || "").trim())}
                          title="Потврди"
                          onChange={(e) => {
                            if (e.target.checked) {
                              confirmActualRow(i);
                            } else {
                              // Отштиклирано = празно: и количината и потврдата се бришат.
                              // patchActual по дефолт ги брише и „Работел"/„Проверил" — редот се
                              // враќа целосно празен, бидејќи испразнет ред не смее да остане
                              // „проверен".
                              const updated = rowsRef.current.map((r, idx) => idx === i ? {
                                ...r,
                                measuredQty: "",
                                confirmed: false,
                                isPreviewActual: false,
                                workedByName: "",
                                workedAt: "",
                                verifiedByName: "",
                                verifiedAt: "",
                                verifyRequested: false,
                              } : r);
                              if (onChangeAndSave) onChangeAndSave(updated);
                              else onChange(updated);
                            }
                          }}
                          {...(!(isSorted || row.confirmed) ? reg(i, 1) : {})}
                        />
                      </td>
                    )}
                    {!isColHidden("serialNumber") && (
                      <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5">
                        {!(row.serialNumber || "").trim() && (
                          <RNRazmerLotLines suggestion={lotSuggestions?.[row.osRowId || ""]} field="serial" unitCode={row.unitCode} />
                        )}
                        <input
                          type="text"
                          className={`${isSorted ? readOnlyCellBase : cellBase}${lotSuggestions?.[row.osRowId || ""] && !(row.serialNumber || "").trim() ? " absolute inset-0 !w-full !h-full" : ""}`}
                          value={row.serialNumber || ""}
                          list={`rn-razmer-lots-${i}`}
                          readOnly={isSorted || !!row.confirmed}
                          disabled={isSorted || !!row.confirmed}
                          onChange={(e) => {
                            const lot = (lotSuggestions?.[row.osRowId || ""]?.availableLots || []).find((candidate) => candidate.serial === e.target.value);
                            patchActual(i, { serialNumber: e.target.value, expiryDate: lot?.deadline || row.expiryDate });
                          }}
                          {...(!(isSorted || row.confirmed) ? reg(i, 2) : {})}
                        />
                        <datalist id={`rn-razmer-lots-${i}`}>
                          {(lotSuggestions?.[row.osRowId || ""]?.availableLots || []).map((lot) => (
                            <option key={`${lot.serial}-${lot.deadline}`} value={lot.serial}>{lot.deadline}</option>
                          ))}
                        </datalist>
                      </td>
                    )}
                    {!isColHidden("expiryDate") && (
                      <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5">
                        {!(row.serialNumber || "").trim() && !(row.expiryDate || "").trim() && (
                          <RNRazmerLotLines suggestion={lotSuggestions?.[row.osRowId || ""]} field="deadline" />
                        )}
                        <input
                          type="text"
                          className={`${isSorted ? readOnlyCellBase : cellBase}${lotSuggestions?.[row.osRowId || ""] && !(row.serialNumber || "").trim() && !(row.expiryDate || "").trim() ? " absolute inset-0 !w-full !h-full" : ""}`}
                          value={row.expiryDate || ""}
                          readOnly={isSorted || !!row.confirmed}
                          disabled={isSorted || !!row.confirmed}
                          onChange={(e) => patchActual(i, { expiryDate: e.target.value })}
                          {...(!(isSorted || row.confirmed) ? reg(i, 3) : {})}
                        />
                      </td>
                    )}
                    {!isColHidden("waste") && (
                      <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${isSorted ? readOnlyCellBase : cellBase} text-right tabular-nums${actualCellMarkCls(row, "waste")}`}
                          value={row.waste || ""}
                          readOnly={isSorted || !!row.confirmed}
                          disabled={isSorted || !!row.confirmed}
                          onChange={(e) => patchActual(i, { waste: e.target.value })}
                          onBlur={() => onActualCommit?.(i, "waste")}
                          {...(!(isSorted || row.confirmed) ? reg(i, 4) : {})}
                        />
                        <RNRazmerCellSaveMark status={actualCellSaveStatus(row, "waste")} />
                      </td>
                    )}
                    {!isColHidden("rastur") && (
                      <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${isSorted ? readOnlyCellBase : cellBase} text-right tabular-nums${actualCellMarkCls(row, "rastur")}`}
                          value={row.rastur || ""}
                          readOnly={isSorted || !!row.confirmed}
                          disabled={isSorted || !!row.confirmed}
                          onChange={(e) => patchActual(i, { rastur: e.target.value })}
                          onBlur={() => onActualCommit?.(i, "rastur")}
                          {...(!(isSorted || row.confirmed) ? reg(i, 5) : {})}
                        />
                        <RNRazmerCellSaveMark status={actualCellSaveStatus(row, "rastur")} />
                      </td>
                    )}
                    {!isColHidden("workedBy") && (
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700">
                        {[row.workedByName, formatDate(row.workedAt)].filter(Boolean).join(" / ")}
                      </td>
                    )}
                    {!isColHidden("verifiedBy") && (
                      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="w-4 h-4 flex-shrink-0 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={isVerified || !!row.verifyRequested}
                            disabled={isSorted || isSaving || !canVerify || isVerified}
                            title={isVerified ? "Проверено" : "Провери ред"}
                            onChange={(e) => setVerifyRequested(i, e.target.checked)}
                            {...(!isSorted && canVerify && !isVerified ? reg(i, 6) : {})}
                          />
                          <span className={row.verifyRequested && !isVerified ? "text-blue-700" : ""}>
                            {row.verifiedByName || row.verifiedAt
                              ? [row.verifiedByName, formatDate(row.verifiedAt)].filter(Boolean).join(" / ")
                              : row.verifyRequested
                                ? "За проверка"
                                : ""}
                          </span>
                        </div>
                      </td>
                    )}
                  </>
                ) : (
                  <>
                    <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                      {row.valuationPrice}
                    </td>
                    <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700">
                      {row.lastPurchasePrice}
                    </td>
                  </>
                )}
              </tr>
              {!isSorted && displayIdx === lastPripremaDisplayIdx && renderKgTotalRow()}
              </React.Fragment>
              );
            })}
          </tbody>
          {/* Normally the total sits inline at the end of the Припрема block, right
              above the double-line process divider (see the tbody map above) — it
              sums ONLY Припрема rows, so the bottom of the table was the wrong place
              for it. This tfoot is the fallback for the two cases where that inline
              slot doesn't exist: sorting breaks process-group contiguity (no divider
              to sit above), or there are no Припрема rows at all. */}
          {(isSorted || lastPripremaDisplayIdx === -1) && (
          <tfoot>
            {renderKgTotalRow()}
          </tfoot>
          )}
        </table>
      </div>
      {enableColumnManager && (
        <ColumnManager
          hiddenColumns={hiddenColumns}
          onColumnToggle={onColumnToggle}
          onColumnDrop={onColumnDrop}
          columns={RN_SIMPLE_RAZMER_COLUMNS}
        />
      )}
    </OSCard>
  );
};

export default OSRazmerTable;
